import * as React from 'react';
import { RvInput } from './Input';
import glamorous from 'glamorous';
import { AppStateUpdate, showNotification, RouterHistoryProps } from '../App';
import { FormEvent } from 'react';
import { RvButton } from './Button';
import gql from 'graphql-tag';
import { client } from '..';
import { Error } from './Error';
import { RvLoader } from './Loader';
import { MatterTerminology } from '../MatterTerminology';
import { ForgottenPassword } from './ForgottenPassword';
import { PreloadComponents } from './LoadComponentAsync';
import { ChangePassword } from './ChangePassword';
import { setTimeout } from 'timers';
import { LeapFirmProvisionStatusType, GetLeapFirmProvisionStatusType } from './Leap/LeapFirmProvisionStatusType';
import { UserFeature, UserFeatureEnum } from '../types/UserFeature';
import {
    Dialog, DialogContent, DialogActions, Button,
    TextField, InputAdornment, IconButton, Checkbox, FormControlLabel, Link, CircularProgress,
} from '@material-ui/core';
import { createTheme, ThemeProvider } from '@material-ui/core/styles';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@material-ui/icons/VisibilityOffOutlined';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import { AppLogo } from './AppHeader/AppLogo';
import { AppFooter } from './AppFooter';
import { RvPanel } from './Panel';
import { SupportedBrowsers } from './SupportedBrowsers';
import { FetchPolicy } from 'apollo-client';
import { GetSmokeballFirmProvisionStatusType, SmokeballFirmProvisionStatusType } from './smokeball/SmokeballFirmProvisionStatusType';
import { ActionStepFirmProvisionStatusType, GetActionStepFirmProvisionStatusType } from './actionStep/ActionStepFirmProvisionStatusType';
import { GetAffinityFirmProvisionStatusType, AffinityFirmProvisionStatusType } from './affinity/AffinityFirmProvisionStatusType';

// ─── MUI theme (login page only — matches the new portal form design system) ───
// Accent blue (#1667C7) matches the app's own "Access & Identity" token (--dc-access-fg / --rv-accent-blue) —
// login is literally the access/identity surface, so it inherits that category's colour rather than picking a new one.
const loginInk = '#1B2233';
const loginSlate = '#5B6472';
const loginHairline = '#E4E7EC';

const loginTheme = createTheme({
    palette: {
        primary: { main: '#1667C7' },
        error: { main: '#C4314B' },
        text: { primary: loginInk, secondary: loginSlate },
        background: { default: '#FAFAF9' },
    },
    shape: { borderRadius: 6 },
    typography: {
        fontFamily: `'Nunito', "Segoe UI", Roboto, Helvetica, Arial, sans-serif`,
        button: { textTransform: 'none', fontWeight: 700 },
    },
    props: {
        MuiTextField: { size: 'small', variant: 'outlined', fullWidth: true },
        MuiButton: { disableElevation: true },
    },
    overrides: {
        MuiButton: {
            root: {
                height: 40,
                transition: 'background-color 150ms, transform 100ms',
                '&:active': {
                    transform: 'scale(0.98)',
                },
                '@media (prefers-reduced-motion: reduce)': {
                    transition: 'none',
                    '&:active': { transform: 'none' },
                },
            },
            containedPrimary: {
                '&:hover': {
                    backgroundColor: '#125AAD',
                },
            },
        },
        MuiOutlinedInput: {
            root: {
                transition: 'border-color 150ms, background-color 150ms',
                '@media (prefers-reduced-motion: reduce)': {
                    transition: 'none',
                },
                // Chrome/Edge paint autofilled inputs with their own light-blue background,
                // ignoring the site's CSS `background-color` entirely - the only override that
                // actually works is a huge inset box-shadow the same colour as the field, which
                // visually paints over it. Explicit request: keep a tinted fill rather than
                // painting it plain white, so a saved-credentials field still reads as visually
                // distinct from one the person just typed into - but use this page's own accent
                // tint instead of Chrome's own raw pastel. `#E3EDF8` is --rv-accent-blue-bg
                // (rgba(22,103,199,0.12)) pre-blended over white as a flat, opaque hex - not the
                // rgba var itself: the input's own box-shadow and the root's background below
                // both cover this same area, so using the translucent rgba in both stacked twice
                // and came out visibly darker/more saturated over the input than over the icon
                // gutters (reported as "colours are still not the same"). An opaque colour
                // painted twice is a no-op, so this keeps both layers identical everywhere.
                '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus': {
                    WebkitBoxShadow: '0 0 0 1000px #E3EDF8 inset',
                    WebkitTextFillColor: loginInk,
                    caretColor: loginInk,
                    transition: 'background-color 5000s ease-in-out 0s',
                },
                // The box-shadow trick above only paints the `<input>` element's own rectangle -
                // the person/lock/eye icon adornments either side of it are separate sibling
                // elements, so without this the tint stopped short of them (a tinted strip in
                // the middle with plain white gutters either side, visible in the reported
                // screenshot). :has() lets the *root* pick up the same tint whenever the input
                // inside it is autofilled, so icons and field share one continuous fill -
                // supported by every browser on the app's own supported-browsers list.
                '&:has(input:-webkit-autofill)': {
                    backgroundColor: '#E3EDF8',
                    borderRadius: 6,
                },
            },
        },
    },
});

// ─── Layout ──────────────────────────────────────────────────────────────────

const mobileBreakpoint = '@media (max-width: 900px)';
const compactBreakpoint = '@media (max-width: 400px)';

const PageRoot = glamorous.div({
    display: 'flex',
    flexDirection: 'row',
    flex: '1 1 auto',
    minHeight: 0,
    width: '100%',
    backgroundColor: '#FAFAF9',
});

// Faint ruled-paper hairlines behind the login column — a quiet nod to a ledger page, never competing for attention.
// Explicit request: an even 50/50 split with PromoPanel below, not a fixed 480px column with
// the static page taking up whatever width was left over.
const LoginColumnWrapper = glamorous.div({
    flex: '1 1 50%',
    width: '50%',
    maxWidth: '100%',
    display: 'flex',
    flexDirection: 'column',
    padding: '32px',
    boxSizing: 'border-box',
    backgroundColor: '#FAFAF9',
    backgroundImage: 'repeating-linear-gradient(' +
        '0deg, rgba(27,34,51,0.035) 0px, rgba(27,34,51,0.035) 1px, transparent 1px, transparent 29px)',
    [mobileBreakpoint]: {
        flex: '1 1 auto',
        width: '100%',
    },
    [compactBreakpoint]: {
        padding: '20px',
    },
});

const LoginFormWrapper = glamorous.div({
    width: '100%',
    // Explicit request: a bit bigger/wider - was 340px, sized back when the login column was a
    // fixed 480px; now that column is a 50%-wide split, the old cap left it looking small and
    // lost in the extra space.
    maxWidth: '420px',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
    justifyContent: 'center',
});

const LoginCard = glamorous.div({
    backgroundColor: '#FFFFFF',
    border: `1px solid ${loginHairline}`,
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(27,34,51,0.06), 0 8px 24px rgba(27,34,51,0.05)',
    padding: '28px 28px 24px',
    boxSizing: 'border-box',
    [compactBreakpoint]: {
        padding: '20px 18px 18px',
    },
});

const FieldStack = glamorous.div({
    '& > *:not(:last-child)': {
        marginBottom: '12px',
    },
});

const LogoBlock = glamorous.div({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: '20px',
});

// tslint:disable-next-line:no-any
const TenantTitleText = glamorous.div<{ theme?: any }>({
    display: 'inline-flex',
    textAlign: 'center',
    marginTop: '10px',
    padding: '3px 10px',
    fontFamily: `Consolas, 'SFMono-Regular', Menlo, monospace`,
    fontSize: '10.5px',
    fontWeight: 600,
    letterSpacing: '0.6px',
    textTransform: 'uppercase',
    color: loginSlate,
    backgroundColor: '#FAFAF9',
    border: `1px solid ${loginHairline}`,
    borderRadius: '99px',
});

const WelcomeHeading = glamorous.div({
    fontSize: '22px',
    fontWeight: 800,
    letterSpacing: '-0.2px',
    color: loginInk,
});

const AccentRule = glamorous.div({
    width: '28px',
    height: '3px',
    borderRadius: '2px',
    backgroundColor: '#1667C7',
    margin: '8px 0 12px',
});

const WelcomeSubheading = glamorous.div({
    fontSize: '12.5px',
    color: loginSlate,
    marginBottom: '18px',
});

const RememberRow = glamorous.div({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    margin: '12px 0',
});

// tslint:disable-next-line:no-any
const LoginButton = glamorous.div<{ theme?: any }>((props) => ({
    display: 'inline-block',
    width: '100%',
    borderTop: '1px solid ' + props.theme.NeutralColors.LightBlue1,
    paddingTop: '20px',
    marginTop: '20px',
    [`& .${`button`}`]: {
        color: props.theme.NeutralColors.BackgroundWhite,
        backgroundColor: props.theme.BrandColors.TemplatePrimary,
        padding: '17px 80px',
    },
}));

const SubmitButtonWrapper = glamorous.div({
    marginTop: '16px',
});

const SupportRow = glamorous.div({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '20px',
    marginTop: '20px',
    '& a': {
        fontSize: '13px',
        fontFamily: 'inherit',
        color: loginSlate,
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
    },
});

const InfoTriggerWrapper = glamorous.div({
    display: 'inline-flex',
    '& .button': {
        background: 'none !important',
        border: 'none !important',
        boxShadow: 'none !important',
        padding: '0 !important',
        margin: '0 !important',
        minHeight: 'auto !important',
        display: 'inline-flex !important',
        alignItems: 'center !important',
        gap: '4px',
        fontSize: '13px !important',
        fontFamily: 'inherit !important',
        lineHeight: 'normal !important',
        color: '#616161 !important',
        fontWeight: '400 !important',
        textTransform: 'none !important',
    },
});

const LoginFooterWrapper = glamorous.div({
    marginTop: 'auto',
    paddingTop: '16px',
    fontSize: '11px',
    color: '#8A8A8A',
});

const PromoPanel = glamorous.div({
    // Matches LoginColumnWrapper's own '1 1 50%' above - explicit request for an even 50/50
    // split, not this side just absorbing whatever width the fixed-width login column left over.
    flex: '1 1 50%',
    position: 'relative',
    backgroundColor: '#EAF2FB',
    display: 'flex',
    [mobileBreakpoint]: {
        display: 'none',
    },
});

const PromoBadge = glamorous.div({
    position: 'absolute',
    top: '20px',
    right: '20px',
    backgroundColor: '#FFFFFF',
    border: '1px solid #D3E2F3',
    borderRadius: '99px',
    padding: '4px 12px',
    fontSize: '11px',
    color: '#5B7EA8',
    zIndex: 1,
});

const PromoIframe = glamorous.iframe({
    width: '100%',
    height: '100%',
    minHeight: '400px',
    border: 'none',
    display: 'block',
    // Explicit bug report: Chromium's default UA style for <iframe> is overflow:clip, not
    // auto/scroll - with no scrollbar of its own, the embedded companion page (taller than
    // this box) was hard-cut wherever its content happened to fall at the box's bottom edge,
    // showing as a stray dark bar (one of that page's own sections, clipped mid-way) instead
    // of a normal cutoff a person could scroll past.
    overflowY: 'auto',
});

const content = `content`;
const DisplayErrors = glamorous.div({
    display: 'block',
    height: '80px',
    justifyContent: 'center',
    width: '100%',
    [`& .${content}`]: {
        textAlign: 'left',
    },
});

const ErrorContainer = glamorous.div({
    display: 'inline-block',
    width: '100%',
    padding: '10px 0 0 0',
});

const DialogDiv = glamorous(Dialog)({
    // width: '100%',
    // minWidth: '400px',
    // minHeight: '400px'
});

interface LoginProps extends RouterHistoryProps {
    match: {
        params: {
            tenantId: string
            command: string
        }
    };
    onLogin?: (isAuthenticated: boolean) => void;
    onTenant?: (tenantId: string) => void;
    onForceToChangePass?: (isChange: boolean) => void;
    onRequestSupport?: () => void;
}

interface LoginState {
    userId: string;
    password: string;
    tenantId: string;
    rememberMe: boolean;
    autoLogin: boolean;
    mfaCodeRequired: boolean;
    isForceChangePassword: boolean;
    mfaCode: string;

    splashScreenTemplate?: string;
    isOpenSplashScreen: boolean;
    showPassword: boolean;

    logo?: string;
    logoUrl?: string;
    tenantTitle?: string;
}

export interface TokenResponse {
    accessToken: string;
    refreshToken: string;
    error: string;
    errorDescription: string;
    expiresIn: number;
    isError: boolean;
    statusCode: number;
    authenticationMethod: string;
    forceChangePassword: boolean;
}

interface OneTimePassword  {
    accessToken: string;
    refreshToken: string;
    expiresIn: number;
    statusCode: number;
    isError: boolean;
    errorDescription: string;
    error: string;
    forceChangePassword: boolean;
}

export default class Login extends React.Component<LoginProps, LoginState> {
    private isLoggingIn: boolean;
    private isLoggedInMfa: boolean;
    private errorLogin: boolean;
    private errorMfa: boolean;
    private errorSummary: string;
    private errorDescription: string;
    private refreshToken: string;
    private userIdToken: string;
    private userNameEl: HTMLInputElement | null = null;
    private passwordEl: HTMLInputElement | null = null;
    private loginButton: RvButton | null;
    private loginButtonEl: HTMLButtonElement | null = null;
    private mfaCode: RvInput | null;
    // tslint:disable-next-line: no-any
    private appUser: any;

    // tslint:disable-next-line:no-any
    constructor(props: LoginProps, context?: any) {
        super(props, context);

        var tenantId = (this.props.match && this.props.match.params.tenantId)
                        ? this.props.match.params.tenantId
                        : '';
        this.refreshToken = 'refreshToken_' + tenantId;
        this.userIdToken = 'userId_' + tenantId;
        var rememberMe = (this.getRefreshToken() != null);
        var userId = this.getUserId();
        this.isLoggingIn = false;
        this.isLoggedInMfa = false;

        // Clear token if refreshing login page
        // sessionStorage.removeItem('tenantId');
        // sessionStorage.setItem('accessToken', '');
        AppStateUpdate.loggedOn = false;

        this.state = {
            userId: userId != null ? userId : '',
            password: '',
            tenantId: tenantId,
            rememberMe: rememberMe,
            autoLogin: rememberMe,
            mfaCodeRequired: false,
            mfaCode: '',
            isForceChangePassword: false,
            isOpenSplashScreen: false,
            showPassword: false
        };

        this.handleRemberMe = this.handleRemberMe.bind(this);
        this.handleClick = this.handleClick.bind(this);
        this.handleMfaClick = this.handleMfaClick.bind(this);
        this.handleKeyDown = this.handleKeyDown.bind(this);
        this.handlePasswordResetSuccess = this.handlePasswordResetSuccess.bind(this);
        this.handlePasswordResetFailure = this.handlePasswordResetFailure.bind(this);
        this.navigateToPage = this.navigateToPage.bind(this);
        // this.fetchUserSplashScreen = this.fetchUserSplashScreen.bind(this);
        this.onCloseSplashScreen = this.onCloseSplashScreen.bind(this);
        this.onToggleShowPassword = this.onToggleShowPassword.bind(this);
    }

    componentDidMount() {
         /*
        var appInitState = AppStateUpdate.getAppState();
        if (appInitState.tenant !== this.state.tenantId) {
            appInitState.tenant = this.state.tenantId;
            AppStateUpdate.updateAppState(appInitState);
        } */
        if (this.props.onTenant) { this.props.onTenant(this.state.tenantId); }
        this.fetchTenantBranding();
        if (this.state.autoLogin) {
            this.loginRemember();
        }

        const autoSignInError = sessionStorage.getItem('autoSignInError');

        if (autoSignInError === 'expired') {
            showNotification(
                'Login Failure',
                'This sign-in link is no longer valid. Please log in to RedView to continue.',
                'warning'
            );
            sessionStorage.removeItem('autoSignInError');
        }

        // if user is already logged in and hit the login url again
        if (sessionStorage.getItem('tenantId') === this.state.tenantId &&
            sessionStorage.getItem('accessToken') !== null &&
            sessionStorage.getItem('accessToken') !== '' &&
            sessionStorage.getItem('isLoggedIn') !== null) {
            this.isLoggingIn = true;
            this.isLoggedInMfa = true;

            if (this.props.onLogin) {
                this.props.onLogin(true);
            }
            this.checkForTenantAutoProvisioning();
        } else {
            // if user tries different tenant without logging out previous one, wipe out local and session storage data
            sessionStorage.removeItem('accessToken');
            sessionStorage.removeItem('refreshTokenForAutoRefresh');
            UserFeature.Clear();
            sessionStorage.removeItem('tenantId');

            let length = localStorage.length;

            while (length--) {
                let key = localStorage.key(length) || '';
                if (/refreshToken_/.test(key)) {
                    localStorage.removeItem(key);
                }
            }
        }

        setTimeout(() => {
            if (this.userNameEl && !this.validateString(this.state.userId)) {
                this.userNameEl.focus();
            } else if (this.passwordEl && this.validateString(this.state.userId)) {
                this.passwordEl.focus();
                this.setState({ rememberMe: true });
            } else if (this.loginButton) {
                this.loginButton.focus();
            } else if (this.loginButtonEl) {
                this.loginButtonEl.focus();
            } else if (this.mfaCode) {
                this.mfaCode.focus();
            }
            // tslint:disable-next-line:align
        }, 500);

        setTimeout(() => {
            PreloadComponents();
            // tslint:disable-next-line:align
        }, 1000);
    }

    handleResendCode = (e: { preventDefault: () => void; }) => {
        e.preventDefault();
        this.resendVerificationCode();
      }

    public render() {
        /*
        if (this.props.data && !this.props.data.loading &&
            this.props.data.tenant && !this.props.data.tenant.tenantId) {
            window.location.replace('http://www.redview.com.au/');
            return <div>Redirecting to RedView....</div>;
        } */

        if (this.state.autoLogin) {
            return (<RvLoader size="large" />);
        }

        let errorMessage;
        if (this.errorLogin && (this.errorSummary || this.errorDescription)) {
            errorMessage = (
                <ErrorContainer>
                    <DisplayErrors>
                        <Error
                            headerMessage={this.errorSummary}
                            message={this.errorDescription}
                        />
                    </DisplayErrors>
                </ErrorContainer>
            );
        } else {
            errorMessage = '';
        }

        if (this.errorMfa && (this.errorSummary || this.errorDescription)) {
            errorMessage = (
                <ErrorContainer>
                    <DisplayErrors>
                        <Error
                            headerMessage={this.errorSummary}
                            message={this.errorDescription}
                        />
                    </DisplayErrors>
                </ErrorContainer>
            );
        }

        // tslint:disable-next-line:no-any
        let inputs: any;

        if (this.state.mfaCodeRequired) {
            // MFA
            inputs = (
                <React.Fragment>
                    {MatterTerminology.MultifactorAuthenticationMessage}
                    <RvInput
                        iconKey="key"
                        hint={MatterTerminology.AuthenticationCode}
                        value={this.state.mfaCode}
                        onChange={this.changeMfaCode}
                        disabled={this.isLoggedInMfa}
                        error={this.errorMfa}
                        onKeyDown={this.handleKeyDownMfa}
                        focus={true}
                        ref={(item) => this.mfaCode = item}
                        iconPosition="left"
                        size="big"
                    />
                </React.Fragment>
            );

        } else if (this.state.isForceChangePassword) {
            // Force change password component
            inputs = (
                <React.Fragment>
                    <ChangePassword
                        handlePasswordResetSuccess={this.handlePasswordResetSuccess}
                        handlePasswordResetFailure={this.handlePasswordResetFailure}
                        isForcedPasswordChange={true}
                        userName={this.state.userId}
                        tenantId={this.state.tenantId}
                    />
                </React.Fragment>
            );
        } else {
            // Usual login process inputs
            inputs = (
                <React.Fragment>
                    <TextField
                        label={MatterTerminology.Username}
                        value={this.state.userId}
                        // tslint:disable-next-line:no-any
                        onChange={(event: any) => this.changeUserId(event)}
                        // tslint:disable-next-line:no-any
                        onKeyDown={(event: any) => this.handleKeyDown(event)}
                        disabled={this.isLoggingIn}
                        error={this.errorLogin}
                        autoFocus={true}
                        inputRef={(item: HTMLInputElement | null) => this.userNameEl = item}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <PersonOutlineIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <TextField
                        label={MatterTerminology.Password}
                        type={this.state.showPassword ? 'text' : 'password'}
                        value={this.state.password}
                        // tslint:disable-next-line:no-any
                        onChange={(event: any) => this.changePassword(event)}
                        // tslint:disable-next-line:no-any
                        onKeyDown={(event: any) => this.handleKeyDown(event)}
                        disabled={this.isLoggingIn}
                        error={this.errorLogin}
                        inputRef={(item: HTMLInputElement | null) => this.passwordEl = item}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <LockOutlinedIcon fontSize="small" />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        size="small"
                                        onClick={this.onToggleShowPassword}
                                        aria-label={this.state.showPassword ? 'Hide password' : 'Show password'}
                                        tabIndex={-1}
                                    >
                                        {this.state.showPassword ? <VisibilityOffOutlinedIcon fontSize="small" /> : <VisibilityOutlinedIcon fontSize="small" />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                </React.Fragment>
            );
        }

        // tslint:disable-next-line:no-any
        let buttons: any;
        if (this.state.mfaCodeRequired) {
            // MFA Button
            buttons = (
                <LoginButton>
                    <RvButton
                        label={MatterTerminology.Verify}
                        onClick={this.handleMfaClick}
                        disabled={this.isLoggedInMfa}
                        loading={this.isLoggedInMfa}
                        ref={(item) => this.loginButton = item}
                    />
                </LoginButton>
            );
        } else if (this.state.isForceChangePassword) {
            // No button is required for Force Change password.
            buttons = null;
        } else {
            // Usual Login button
            buttons = (
                <SubmitButtonWrapper>
                    <Button
                        variant="contained"
                        color="primary"
                        fullWidth={true}
                        type="submit"
                        disabled={this.isLoggingIn}
                        onClick={this.handleClick}
                        ref={(item: HTMLButtonElement | null) => this.loginButtonEl = item}
                        startIcon={this.isLoggingIn ? <CircularProgress color="inherit" size={16} /> : undefined}
                    >
                        {this.isLoggingIn ? 'Logging in…' : 'Log in'}
                    </Button>
                </SubmitButtonWrapper>
            );
        }

        // tslint:disable-next-line:no-any
        let controls: any;

        if (this.state.mfaCodeRequired) {
            // <FA
            controls =  (
                // eslint-disable-next-line jsx-a11y/anchor-is-valid
                <a href="#" onClick={this.handleResendCode} >
                    {MatterTerminology.ResendVerificationCode}
                </a>
            );
        } else if (this.state.isForceChangePassword) {
            controls = null;
        } else {
            // Usual Login process
            controls = (
                <RememberRow>
                    <FormControlLabel
                        control={
                            <Checkbox
                                size="small"
                                color="primary"
                                checked={this.state.rememberMe}
                                onChange={this.handleRemberMe}
                            />
                        }
                        label={<span style={{ fontSize: '13px' }}>{MatterTerminology.RememberMe}</span>}
                    />
                    <ForgottenPassword
                        userID={this.state.userId}
                        tenantId={this.state.tenantId}
                        renderTrigger={(onClick) => (
                            <Link
                                component="button"
                                type="button"
                                variant="body2"
                                underline="hover"
                                onClick={onClick}
                                style={{ fontSize: '13px' }}
                            >
                                {MatterTerminology.ForgottenPassword}
                            </Link>
                        )}
                    />
                </RememberRow>
            );

        }

        return (
            <ThemeProvider theme={loginTheme}>
                <PageRoot>
                    <LoginColumnWrapper>
                        <LoginFormWrapper>
                            <LoginCard>
                                <LogoBlock>
                                    <AppLogo logoImg={this.state.logo} logoUrl={this.state.logoUrl} />
                                    {this.state.tenantTitle && (
                                        <TenantTitleText>{this.state.tenantTitle}</TenantTitleText>
                                    )}
                                </LogoBlock>

                                <WelcomeHeading>Welcome back</WelcomeHeading>
                                <AccentRule />
                                <WelcomeSubheading>Log in to continue to your portal.</WelcomeSubheading>

                                <FieldStack>
                                    {inputs}
                                </FieldStack>

                                {controls}
                                {errorMessage}
                                {buttons}
                            </LoginCard>

                            <SupportRow>
                                <Link
                                    component="button"
                                    type="button"
                                    variant="body2"
                                    underline="hover"
                                    onClick={() => { if (this.props.onRequestSupport) { this.props.onRequestSupport(); } }}
                                    style={{ fontSize: '13px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                                >
                                    <HelpOutlineIcon fontSize="inherit" /> Get Support
                                </Link>
                                <InfoTriggerWrapper>
                                    <RvPanel iconKey="info circle" size="small" label="Supported browsers">
                                        <SupportedBrowsers />
                                    </RvPanel>
                                </InfoTriggerWrapper>
                            </SupportRow>
                        </LoginFormWrapper>

                        <LoginFooterWrapper>
                            <AppFooter compact={true} />
                        </LoginFooterWrapper>
                    </LoginColumnWrapper>

                    <PromoPanel>
                        <PromoIframe
                            src="https://www.redraincorp.com/electronic-intake-forms"
                            title="RedView Promotions"
                            scrolling="auto"
                        />
                        <PromoBadge>From redraincorp.com</PromoBadge>
                    </PromoPanel>

                    {this.getSplashScreen()}
                </PageRoot>
            </ThemeProvider>
        );
    }

    private handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        this.login();
    }

    private handleMfaClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        this.multifactorAuthentication();
    }

    private validateString = (value: string, name?: string): boolean => {
        if (value == null || value.trim().length === 0) {
            if (name) {
                showNotification(null, `"${name}" can not be blank`, 'warning');
            }
            return false;
        }
        return true;
    }

    private login = () => {
        this.clearRefreshToken();

        if (!this.validateString(this.state.tenantId, 'tenant') ||
            !this.validateString(this.state.userId, 'user name') ||
            !this.validateString(this.state.password, 'password')) {
            return;
        }

        this.isLoggingIn = true;

        client.mutate({
            mutation: LoginMutation,
            variables: {
                tenantId: this.state.tenantId,
                userName: this.state.userId,
                password: this.state.password
            }
        }).then((results: {
            data: {
                login: TokenResponse
            }
        }) => {
            var response = results.data.login;
            if (!this.loginSuccess(response, this.state.rememberMe, this.state.userId)) {
                if (response.statusCode === 404) {
                    this.isLoggingIn = false;
                    this.errorLogin = true;
                    this.errorSummary = 'Invalid Tenant';
                    this.forceUpdate();
                } else {
                    this.isLoggingIn = false;
                    this.errorLogin = true;
                    this.errorSummary = 'Failed to Login';
                    this.errorDescription = response.errorDescription;
                    this.forceUpdate();
                }
            }

        // tslint:disable-next-line:no-any
        }).catch((reason: any) => {
            this.isLoggingIn = false;
            this.errorLogin = true;
            this.errorSummary = 'System Error';
            this.errorDescription = reason;
            this.forceUpdate();
        });
    }

    /**
     * Used to Verify the Authentication code in Multifactor Authentication
     */
    private multifactorAuthentication = () => {
        if (!this.validateString(this.state.tenantId, 'tenant') ||
            !this.validateString(this.state.mfaCode, 'authentication code')) {
            return;
        }

        this.isLoggedInMfa = true;
        // this.forceUpdate();

        client.mutate({
            mutation: MultifactorAuthenticationMutation,
            variables: {
                tenantId: this.state.tenantId,
                otp: this.state.mfaCode,
                token: sessionStorage.getItem('accessToken')
            }
        }).then((results: {
            data: {
                otp: OneTimePassword
            }
        }) => {
            var response = results.data.otp;
            if (!this.loginMfaSuccess(response, this.state.rememberMe, this.state.userId)) {
                    if (response.statusCode === 404) {
                        this.isLoggedInMfa = false;
                        this.errorMfa = true;
                        this.errorSummary = 'Invalid Tenant';
                        this.forceUpdate();
                    } else {
                        this.isLoggedInMfa = false;
                        this.errorMfa = true;
                        this.errorSummary = 'Failed to verify';
                        this.errorDescription = MatterTerminology.VerificationFailed;
                        this.forceUpdate();
                    }
                }
        // tslint:disable-next-line:no-any
        }).catch((reason: any) => {
            this.isLoggedInMfa = false;
            this.errorMfa = true;
            this.errorSummary = 'System Error';
            this.errorDescription = reason;
            this.forceUpdate();
        });
    }

    /**
     * Used to resend verification code
     */
    private resendVerificationCode = () => {

        if (!this.validateString(this.state.tenantId, 'tenant')) {
            return;
        }

        this.isLoggedInMfa = true;

        client.mutate({
            mutation: SendVerificationCodeMutation,
            variables: {
                tenantId: this.state.tenantId,
                token: sessionStorage.getItem('accessToken')
            }
        }).then((results: {
            data: {
                resendOtp: OneTimePassword
            }
        }) => {
            var response = results.data.resendOtp;

            if (response.statusCode === 200) {
                this.isLoggedInMfa = false;
                sessionStorage.setItem('accessToken', response.accessToken);
                UserFeature.Init();
                showNotification(null, MatterTerminology.VerificationCodeResendSuccessMessage, 'success');

            } else {
                if (response.statusCode === 404) {
                    this.isLoggedInMfa = false;
                    this.errorMfa = true;
                    this.errorSummary = 'Invalid Tenant';
                    this.forceUpdate();
                } else {
                    this.isLoggedInMfa = false;
                    this.errorMfa = true;
                    this.errorSummary = 'Failed to resend verification code';
                    this.errorDescription = response.errorDescription;
                    this.forceUpdate();
                }
            }

            return true;
        // tslint:disable-next-line:no-any
        }).catch((reason: any) => {
            this.isLoggedInMfa = false;
            this.errorMfa = true;
            this.errorSummary = 'System Error';
            this.errorDescription = reason;
            this.forceUpdate();
        });
    }

    private handleRemberMe = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
        this.setState({ rememberMe: checked });
        if (!checked) {
            this.clearRefreshToken();
        }
    }

    private changeUserId = (event: FormEvent<HTMLInputElement>) => {
        var target = event.target as HTMLInputElement;
        this.setState({ userId: target.value });
    }

    private changePassword = (event: FormEvent<HTMLInputElement>) => {
        var target = event.target as HTMLInputElement;
        this.setState({ password: target.value });
    }

    private changeMfaCode = (event: FormEvent<HTMLInputElement>) => {
        var target = event.target as HTMLInputElement;
        this.setState({ mfaCode: target.value });
    }

    private handleKeyDown = (event: KeyboardEvent) => {
        if (event.keyCode === 13) {
            this.login();
        }
    }

    private handleKeyDownMfa = (event: KeyboardEvent) => {
        if (event.keyCode === 13) {
            this.multifactorAuthentication();
        }
    }

    private loginSuccess = (response: TokenResponse, storeToken: boolean, userId: string): boolean => {
        var vm = this;
        if (response.statusCode === 200) {
            this.errorLogin = false;

            if (response.forceChangePassword === true) {
                this.setLoginValuesForChangePassword(response, userId);
                this.setState({
                    isForceChangePassword : true
                });
                return true;
            } else {
                sessionStorage.setItem('accessToken', response.accessToken);
                sessionStorage.setItem('refreshTokenForAutoRefresh', response.refreshToken);
                UserFeature.Init();
                if (storeToken) {
                    this.setRefreshToken(response.refreshToken);
                    this.setUserId(userId);
                } else {
                    this.setUserId('');
                }

                // Set Multifactor authentication required
                if (response.authenticationMethod !== null &&
                    response.authenticationMethod !== undefined &&
                    response.authenticationMethod.length !== 0) {
                    sessionStorage.setItem('mfaCodeRequired', '1');
                    vm.setState({ mfaCodeRequired : true});
                    return true;
                } else {
                    sessionStorage.setItem('mfaCodeRequired', '0');
                    vm.setState({ mfaCodeRequired : false});
                }

                if (response.authenticationMethod === null) {
                    // AppStateUpdate.loggedOn = true;
                    return this.setLoginValues();
                }
            }
        }
        return false;
    }

    private loginMfaSuccess = (response: OneTimePassword, storeToken: boolean, userId: string): boolean => {
        if (response.statusCode === 200) {
            if (response.forceChangePassword) {
                this.setLoginValuesForChangePassword(response, userId);
                this.setState({
                    isForceChangePassword : true,
                    mfaCodeRequired: false
                });
                return true;
            } else {
                sessionStorage.setItem('accessToken', response.accessToken);
                sessionStorage.setItem('refreshTokenForAutoRefresh', response.refreshToken);
                UserFeature.Init();
                if (storeToken) {
                    this.setRefreshToken(response.refreshToken);
                    this.setUserId(userId);
                } else {
                    this.setUserId('');
                }

                return this.setLoginValues();
            }
        }
        return false;
    }

    private setLoginValues() {
        sessionStorage.setItem('tenantId', this.state.tenantId);
        sessionStorage.setItem('isLoggedIn', 'true');

        this.checkForTenantAutoProvisioning();
        return true;
    }

    private redirect() {
        if (this.props.onLogin) {
            this.props.onLogin(true);
        }
    }

    private checkForTenantAutoProvisioning() {
        client.query({
            query: TenantData
            // tslint:disable-next-line:no-any
        }).then((results: {
            // tslint:disable-next-line:no-any
            data: any;
            }) => {
                if (results.data && results.data.tenant && results.data.tenant.sourceSystem === 'Leap' && results.data.tenant.leapProvisioningStatus !== null &&
                    GetLeapFirmProvisionStatusType(results.data.tenant.leapProvisioningStatus) !== LeapFirmProvisionStatusType.DoNotProvision &&
                    GetLeapFirmProvisionStatusType(results.data.tenant.leapProvisioningStatus) !== LeapFirmProvisionStatusType.Complete) {
                    this.props.history.push('/LeapSignIn');
                    this.redirect();
                } else if (results.data && results.data.tenant && results.data.tenant.sourceSystem === 'Smokeball' && results.data.tenant.smokeballProvisioningStatus !== null &&
                    GetSmokeballFirmProvisionStatusType(results.data.tenant.smokeballProvisioningStatus) !== SmokeballFirmProvisionStatusType.DoNotProvision &&
                    GetSmokeballFirmProvisionStatusType(results.data.tenant.smokeballProvisioningStatus) !== SmokeballFirmProvisionStatusType.Complete) {
                    this.props.history.push('/SmokeballSignIn');
                    this.redirect();
                } else if (results.data && results.data.tenant && results.data.tenant.sourceSystem === 'ActionStep' && results.data.tenant.actionStepProvisioningStatus !== null &&
                    GetActionStepFirmProvisionStatusType(results.data.tenant.actionStepProvisioningStatus) !== ActionStepFirmProvisionStatusType.DoNotProvision &&
                    GetActionStepFirmProvisionStatusType(results.data.tenant.actionStepProvisioningStatus) !== ActionStepFirmProvisionStatusType.Complete) {
                    this.props.history.push('/ActionStepSignIn');
                    this.redirect();
                } else if (results.data && results.data.tenant && results.data.tenant.sourceSystem === 'Affinity' && results.data.tenant.affinityProvisioningStatus !== null &&
                    GetAffinityFirmProvisionStatusType(results.data.tenant.affinityProvisioningStatus) !== AffinityFirmProvisionStatusType.DoNotProvision &&
                    GetAffinityFirmProvisionStatusType(results.data.tenant.affinityProvisioningStatus) !== AffinityFirmProvisionStatusType.Complete) {
                    this.props.history.push('/AffinitySignIn');
                    this.redirect();
                } else {
                    // For all other source systems
                    this.setUserData();
                }
            // tslint:disable-next-line:no-any
            }).catch((reason: any) => {
                showNotification(null, reason, 'error');
        });
    }

    private setUserData() {
        let vm = this;

        client.query({
            query: UserData,
            fetchPolicy: 'network-only'
            // tslint:disable-next-line:no-any
        }).then((results: {
            // tslint:disable-next-line:no-any
            data: any;
            }) => {
                // Display Splash Screen
                if (this.state.autoLogin) {
                    vm.manageReDirect(results.data.appUser);
                } else {
                    vm.appUser = results.data.appUser;

                    if (this.state.userId !== undefined && this.state.userId.length > 0) {
                        vm.fetchUserSplashScreen(results.data.appUser);
                    } else {
                        vm.manageReDirect(results.data.appUser);
                    }
                }

                // // check matter access count
                // if (results.data.appUser.matterAccessCount === 1) {
                //     this.navigateToMatterDetails(results.data.appUser.defaultMatterId);
                // } else if (results.data.appUser.isAdministrator) {
                //     // this.props.history.push('/admin');
                //     this.navigateToPage(true);
                // } else {
                //     this.navigateToPage(false);
                //     // this.props.history.push('/matterSummary');
                // }
                // this.redirect();
            // tslint:disable-next-line:no-any
            }).catch((reason: any) => {
                showNotification(null, reason, 'error');
        });
    }

    // tslint:disable-next-line: no-any
    private fetchUserSplashScreen(appUser: any) {
        let vm = this;
        var fetchPolicy: FetchPolicy = 'network-only';

        client.query({
            query: SplashScreenData,
            fetchPolicy: fetchPolicy
            // tslint:disable-next-line:no-any
        }).then((results: {
            // tslint:disable-next-line:no-any
            data: any;
            }) => {
                if (results.data && results.data.appUser &&
                    results.data.appUser.splashScreen &&
                    results.data.appUser.splashScreen.template !== null ) {
                        vm.setState({
                        isOpenSplashScreen: true,
                        splashScreenTemplate: results.data.appUser.splashScreen.template
                    });
                } else {
                    vm.manageReDirect(appUser);
                }
            // tslint:disable-next-line:no-any
            }).catch((reason: any) => {
                showNotification(null, reason, 'error');
        });
    }

    // tslint:disable-next-line: no-any
    private manageReDirect(appUser: any) {
        // check matter access count
        if (appUser.matterAccessCount === 1) {
            this.navigateToMatterDetails(appUser.defaultMatterId);
        } else if (appUser.isAdministrator) {
            // this.props.history.push('/admin');
            this.navigateToPage(true, appUser.isInternalAdmin, appUser.isInternal);
        } else {
            this.navigateToPage(false, appUser.isInternalAdmin, appUser.isInternal);
            // this.props.history.push('/matterSummary');
        }
        this.redirect();
    }

    private getSplashScreen() {
        return (
            <DialogDiv
                open={this.state.isOpenSplashScreen}
                onClose={this.onCloseSplashScreen}
                aria-labelledby="form-dialog-title"
                maxWidth="md"
                fullWidth={true}
                // className={classes.root}
            >
                {/* <DialogTitleDiv
                    id="form-dialog-title"
                    // className={classes.dialogTitle}
                >
                    Splash Screen
                </DialogTitleDiv> */}
                <DialogContent dividers={true}>
                    <React.Fragment>
                        {
                            this.state.splashScreenTemplate
                            ? (<div dangerouslySetInnerHTML={{__html: this.state.splashScreenTemplate}} /> )
                            : null
                        }
                    </React.Fragment>
                </DialogContent>
                <DialogActions>
                    <Button onClick={this.onCloseSplashScreen} color="primary">
                        Close
                    </Button>
                </DialogActions>
            </DialogDiv>
        );
    }

    private onCloseSplashScreen() {
        if (this.appUser) {
            this.manageReDirect(this.appUser);
        }
    }

    private navigateToPage(isAdmin: boolean, isInternalAdmin: boolean, isInternalUser: boolean) {
        if ( (isAdmin && UserFeature.HasAccess(UserFeatureEnum.hasAdminDashboardPage)) || isInternalAdmin) {
            this.props.history.push('/admin');
        } else if (UserFeature.HasAccess(UserFeatureEnum.hasClientDashboard) && isInternalUser === false ) { // if external User, client Dashboard is the first navigation
            this.props.history.push('/clientDashboard');
        } else if (UserFeature.HasAccess(UserFeatureEnum.hasShowUserDashboard) ) {
            this.props.history.push('/userDashboard');
        } else if (UserFeature.HasAccess(UserFeatureEnum.hasFirmDashboard) ) {
            this.props.history.push('/dashboard');
        } else if (UserFeature.HasAccess(UserFeatureEnum.hasIPAttorneyDashboard) ) {
            this.props.history.push('/lawyerDashboard');
        } else if (UserFeature.HasAccess(UserFeatureEnum.hasShowFeeEarnerDashboard) ) {
            this.props.history.push('/feeEarnerDashboard');
        } else if (UserFeature.HasAccess(UserFeatureEnum.hasMatterSummary) ) {
            this.props.history.push('/matterSummary');
        } else if (UserFeature.HasAccess(UserFeatureEnum.hasBulkMatterManagement) ) {
            this.props.history.push('/matterManagement');
        } else if (UserFeature.HasAccess(UserFeatureEnum.hasNameSummary) ) {
            this.props.history.push('/clientsAndContacts');
        } else if (UserFeature.HasAccess(UserFeatureEnum.hasReferrerSummary) ) {
            this.props.history.push('/referrers');
        } else if (UserFeature.HasAccess(UserFeatureEnum.hasStaffSummary) ) {
            this.props.history.push('/staffSummary');
        } else if (UserFeature.HasAccess(UserFeatureEnum.hasTimesheetSummary) ) {
            this.props.history.push('/timesheet');
        } else if (UserFeature.HasAccess(UserFeatureEnum.hasLeadsDashboard) ) {
            this.props.history.push('/enquiriesDashboard');
        } else if (UserFeature.HasAccess(UserFeatureEnum.hasLeadsSummary) ) {
            this.props.history.push('/enquiriesSummary');
        } else if (UserFeature.HasAccess(UserFeatureEnum.hasTaskSummary) ) {
            this.props.history.push('/tasksSummary');
        } else if (UserFeature.HasAccess(UserFeatureEnum.hasLicencedReports) ) {
            this.props.history.push('/reports');
        } else if (UserFeature.HasAccess(UserFeatureEnum.hasInnographyLink) ) {
            this.props.history.push('/innography');
        } else if (UserFeature.HasAccess(UserFeatureEnum.hasMailingList) ) {
            this.props.history.push('/mailingList');
        } else if (UserFeature.HasAccess(UserFeatureEnum.hasClientSenseLink) ) {
            this.props.history.push('/clientsense');
        } else if (UserFeature.HasAccess(UserFeatureEnum.hasConfigurationMaintenance) ) {
            this.props.history.push('/configurationDashboard');
        }
    }

    private navigateToMatterDetails(matterId: number) {
        client.mutate({
            mutation: AddUserMatterAccessActivity,
            variables: {
                matterId: matterId
            }
        }).then((results: {
            data: {
                addUserMatterAccessActivity: boolean
            }
        }) => {
            if (results.data.addUserMatterAccessActivity === false) {
                // tslint:disable-next-line:no-console
                console.log('AddUserMatterAccessActivity mutation failed');
            }
        });

        this.props.history.push('/matterdetail/' + matterId);
    }

    private onToggleShowPassword() {
        this.setState({showPassword: !this.state.showPassword});
    }

    private fetchTenantBranding() {
        const logoStorageKey = this.state.tenantId + '_brandlogo';
        // tslint:disable-next-line:no-any
        const cachedLogo: any = localStorage.getItem(logoStorageKey);
        if (cachedLogo !== null && cachedLogo.length > 0) {
            this.setState({ logo: cachedLogo });
        }

        client.query({
            query: TenantBrandingQuery,
            variables: { tenantId: this.state.tenantId }
            // tslint:disable-next-line:no-any
        }).then((results: { data: any }) => {
            const tenant = results.data && results.data.tenant;
            if (tenant) {
                if (tenant.logo) {
                    localStorage.setItem(logoStorageKey, tenant.logo);
                }
                this.setState({
                    logo: tenant.logo || cachedLogo || undefined,
                    logoUrl: tenant.companyUrl || 'http://www.RedRainCorp.com',
                    tenantTitle: tenant.title || undefined,
                });
            }
        // tslint:disable-next-line:no-any
        }).catch((reason: any) => {
            // Branding is decorative only - keep whatever was cached and don't surface an error for it.
        });
    }

    private setLoginValuesForChangePassword(response: TokenResponse | OneTimePassword, userId: string) {
        sessionStorage.setItem('tenantId', this.state.tenantId);
        sessionStorage.removeItem('isLoggedIn');
        sessionStorage.removeItem('accessToken');
        sessionStorage.removeItem('refreshTokenForAutoRefresh');
        UserFeature.Clear();
        this.setUserId(userId);
    }

    // tslint:disable-next-line:no-any
    private handlePasswordResetSuccess(response: any) {
        sessionStorage.setItem('isLoggedIn', 'true');
        sessionStorage.setItem('accessToken', response.accessToken);
        UserFeature.Init();
        this.setRefreshToken(response.refreshToken);

        this.setLoginValues();
    }

    private handlePasswordResetFailure(failureMessage: string) {
        this.errorSummary = 'Failed to change password';
        this.errorDescription = failureMessage;

        sessionStorage.removeItem('accessToken');
        sessionStorage.removeItem('refreshTokenForAutoRefresh');
        UserFeature.Clear();
        sessionStorage.removeItem('tenantId');
        sessionStorage.removeItem(this.refreshToken);
        sessionStorage.removeItem(this.userIdToken);
        sessionStorage.removeItem('isLoggedIn');
    }

    private clearRefreshToken() {
        this.setState({ autoLogin: false });
        localStorage.removeItem(this.refreshToken);
    }

    private getRefreshToken(): string | null {
        return localStorage.getItem(this.refreshToken);
    }

    private setRefreshToken(data: string) {
        localStorage.setItem(this.refreshToken, data);
    }

    private getUserId(): string | null {
        return localStorage.getItem(this.userIdToken);
    }

    private setUserId(userId: string) {
        localStorage.setItem(this.userIdToken, userId);
    }

    private loginRemember() {
        var refreshToken = this.getRefreshToken();
        if (!refreshToken) {
            this.setState({ autoLogin: false });
            return;
        }
        this.isLoggingIn = true;
        this.forceUpdate();
        var page = this;
        client.mutate({
            mutation: RefreshTokenMutation,
            variables: {
                tenantId: this.state.tenantId,
                refreshToken: refreshToken
            }
        }).then((results: {
            data: {
                refreshToken: TokenResponse
            }
        }) => {
            var response = results.data.refreshToken;
            if (!page.loginSuccess(response, true, this.state.userId)) {
                page.clearRefreshToken();
                page.isLoggingIn = false;
                page.errorLogin = true;
                page.errorSummary = 'Failed to auto login';
                page.errorDescription = 'Invalid Token';
                page.forceUpdate();
            }
        // tslint:disable-next-line:no-any
        }).catch((reason: any) => {
            page.isLoggingIn = false;
            page.errorLogin = true;
            page.clearRefreshToken();
            page.errorSummary = 'Refresh - System error';
            page.errorDescription = reason;
            page.forceUpdate();
        });
    }
}

const LoginMutation = gql`mutation login($tenantId: String!, $userName: String!, $password: String!) {
    login(tenantId: $tenantId, userName: $userName, password: $password) {
      accessToken
      refreshToken
      expiresIn
      statusCode
      isError
      errorDescription
      error
      authenticationMethod
      forceChangePassword
    }
}`;

const MultifactorAuthenticationMutation = gql`mutation otp($tenantId: String!, $otp: String!, $token: String!) {
    otp(tenantId: $tenantId, otp: $otp, token: $token) {
        accessToken
        refreshToken
        expiresIn
        statusCode
        isError
        errorDescription
        error
        forceChangePassword
    }
}`;

const SendVerificationCodeMutation = gql`mutation otp($tenantId: String!, $token: String!) {
    resendOtp(tenantId: $tenantId, token: $token) {
        accessToken
        refreshToken
        expiresIn
        statusCode
        isError
        errorDescription
        error
    }
}`;

export const RefreshTokenMutation = gql`mutation refreshToken($tenantId: String!, $refreshToken: String!) {
    refreshToken(tenantId: $tenantId, refreshToken: $refreshToken) {
      accessToken
      refreshToken
      expiresIn
      statusCode
      isError
      errorDescription
      authenticationMethod
      error
    }
  }`;

const UserData = gql`query AppUser {
    appUser {
        userId
        name
        role
        matterAccessCount
        defaultMatterId
        isAdministrator
        isInternalAdmin
        isInternal
    }
}`;

const TenantData = gql`query Tenant {
    tenant {
        sourceSystem
        leapProvisioningStatus
        smokeballProvisioningStatus
        actionStepProvisioningStatus
        affinityProvisioningStatus
    }
}`;

const TenantBrandingQuery = gql`query TenantBranding($tenantId: String) {
    tenant(tenantId: $tenantId) {
        logo
        companyUrl
        title
    }
}`;

const AddUserMatterAccessActivity = gql`mutation AddUserMatterAccessActivity($matterId: Int) {
    addUserMatterAccessActivity(matterId: $matterId)
}`;

const SplashScreenData = gql`
query splashScreen{
    appUser {
        splashScreen {
            template,
        }
   }
}`;

// export const Login = RVLogin;

// tslint:disable-next-line:no-any
/* export const Login = graphql<any, LoginProps, any>(TenantData, {
    options: ({ match }) => ({ variables: { tenantId: match.params.tenantId } })
})(RvLogin); */
